import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import InfoPopover from '../common/InfoPopover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { effektivPeriode, lastNedBlob, lesFeilmelding } from './auditEksport';
import { adminAuditAPI, superadminAPI, type AdminAuditLogg as AdminAuditLoggType } from '../../services/api';

const formatDate = (value: string): string => {
  return new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));
};

const formatJson = (value: Record<string, unknown> | null): string => {
  if (!value) return 'Ingen data';
  return JSON.stringify(value, null, 2);
};

interface AdminAuditLoggProps {
  /** 'org': organisasjonens logg (admin). 'plattform': plattformloggen (superadmin, hendelser uten organisasjon). */
  kilde: 'org' | 'plattform';
}

const AdminAuditLogg: React.FC<AdminAuditLoggProps> = ({ kilde }) => {
  const erPlattform = kilde === 'plattform';
  const [auditLogg, setAuditLogg] = useState<AdminAuditLoggType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [fra, setFra] = useState(() => effektivPeriode('', '').fra);
  const [til, setTil] = useState(() => effektivPeriode('', '').til);
  const [eksporterer, setEksporterer] = useState(false);

  const fetchAuditLogg = async () => {
    try {
      setLoading(true);
      setError(null);
      const filtre = {
        limit: 50,
        action: actionFilter || undefined,
        resourceType: resourceTypeFilter || undefined,
        ...effektivPeriode(fra, til),
      };
      const response = erPlattform
        ? await superadminAPI.getPlattformAuditLogg(filtre)
        : await adminAuditAPI.getAll(filtre);
      setAuditLogg(response.auditLogg || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke laste audit-logg');
    } finally {
      setLoading(false);
    }
  };

  const eksporter = async () => {
    try {
      setEksporterer(true);
      setError(null);
      const filtre = {
        action: actionFilter || undefined,
        resourceType: resourceTypeFilter || undefined,
        ...effektivPeriode(fra, til),
      };
      const { data, filnavn } = erPlattform
        ? await superadminAPI.eksporterPlattformAuditLogg(filtre)
        : await adminAuditAPI.eksporter(filtre);
      lastNedBlob(data, filnavn || 'audit-logg.json');
    } catch (err: any) {
      setError(await lesFeilmelding(err, 'Kunne ikke eksportere audit-logg'));
    } finally {
      setEksporterer(false);
    }
  };

  useEffect(() => {
    fetchAuditLogg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="flex justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Admin audit-logg</h3>
            <InfoPopover label="Hva er audit-loggen?">
              <p>
                Loggen viser alle handlinger en admin eller superadmin gjør relatert til
                brukere i organisasjonen. Eksempel: oppdatere informasjon om en bruker eller
                slette en bruker.
              </p>
            </InfoPopover>
          </div>
          <p className="text-sm text-muted-foreground">
            {erPlattform
              ? 'Plattform: hendelser uten organisasjon (f.eks. valg av organisasjonskontekst).'
              : 'Viser administrative handlinger for din organisasjon.'}
          </p>
        </div>
        <div className="flex gap-2 items-start">
          <Button variant="outline" onClick={eksporter} disabled={eksporterer}>
            {eksporterer ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Eksporter JSON
          </Button>
          <Button variant="outline" onClick={fetchAuditLogg}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Oppdater
          </Button>
        </div>
      </div>

      <div className="flex gap-4 mb-6 flex-wrap items-end">
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground">Handling</label>
          <Input
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value)}
            placeholder="user.updated"
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground">Ressurstype</label>
          <Input
            value={resourceTypeFilter}
            onChange={(event) => setResourceTypeFilter(event.target.value)}
            placeholder="user"
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="audit-fra" className="text-sm font-medium text-foreground">Fra</label>
          <Input
            id="audit-fra"
            type="date"
            value={fra}
            onChange={(event) => setFra(event.target.value)}
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="audit-til" className="text-sm font-medium text-foreground">Til</label>
          <Input
            id="audit-til"
            type="date"
            value={til}
            onChange={(event) => setTil(event.target.value)}
            className="h-9"
          />
        </div>
        <Button onClick={fetchAuditLogg}>Filtrer</Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>Tidspunkt</TableHead>
                <TableHead>Aktør</TableHead>
                <TableHead>Handling</TableHead>
                <TableHead>Ressurs</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogg.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <p className="text-sm text-muted-foreground">Ingen audit-eventer funnet.</p>
                  </TableCell>
                </TableRow>
              ) : (
                auditLogg.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <TableRow>
                      <TableCell className="w-12">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                        >
                          {expandedId === entry.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>{formatDate(entry.created_at)}</TableCell>
                      <TableCell>{entry.actor_email || (entry.actor_user_id ? `Bruker #${entry.actor_user_id}` : 'Plattform')}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.action}</Badge>
                      </TableCell>
                      <TableCell>
                        {entry.resource_type}
                        {entry.resource_id ? ` #${entry.resource_id}` : ''}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            entry.status === 'success'
                              ? 'bg-status-confirmed text-white hover:bg-status-confirmed'
                              : 'bg-status-waiting text-white hover:bg-status-waiting'
                          }
                        >
                          {entry.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {expandedId === entry.id && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div>
                              <p className="text-sm font-medium mb-1">Før</p>
                              <div className="border rounded-md p-3 bg-muted overflow-auto">
                                <pre className="m-0 text-xs">{formatJson(entry.before_data)}</pre>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium mb-1">Etter</p>
                              <div className="border rounded-md p-3 bg-muted overflow-auto">
                                <pre className="m-0 text-xs">{formatJson(entry.after_data)}</pre>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium mb-1">Metadata</p>
                              <div className="border rounded-md p-3 bg-muted overflow-auto">
                                <pre className="m-0 text-xs">{formatJson(entry.metadata)}</pre>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium mb-1">Request</p>
                              <p className="text-sm">IP: {entry.ip_address || 'Ukjent'}</p>
                              <p className="text-sm">User-agent: {entry.user_agent || 'Ukjent'}</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogg;
